import { useCallback, useMemo } from 'react'
import { usePlatformConfig } from '../contexts/PlatformConfigContext'
import { resolveTerm } from '../data/terminology'
import type { TermConcept } from '../types/install'

export interface Terminology {
  /** One concept, one form. `term('property', 'plural')` → "Properties". */
  term: (concept: TermConcept, form?: 'singular' | 'plural' | 'short') => string
  /**
   * A phrase carrying a concept, written once. `phrase('{housing} management')`
   * resolves the placeholder rather than concatenating words, so a label reads
   * correctly whatever the concept is called. `{property:plural}` selects a form.
   */
  phrase: (template: string) => string
}

/**
 * Reads the deployment's vocabulary. Every business-facing word in
 * administration comes from here rather than a string literal, so renaming a
 * concept is a setting rather than a search through the source.
 */
export function useTerminology(): Terminology {
  const { config } = usePlatformConfig()
  const overrides = config.terminology

  const term = useCallback(
    (concept: TermConcept, form: 'singular' | 'plural' | 'short' = 'singular') =>
      resolveTerm(overrides, concept, form),
    [overrides],
  )

  const phrase = useCallback(
    (template: string) =>
      template.replace(/\{([a-zA-Z]+)(?::(singular|plural|short))?\}/g, (whole, concept, form) => {
        const resolved = resolveTerm(
          overrides,
          concept as TermConcept,
          (form as 'singular' | 'plural' | 'short') ?? 'singular',
        )
        // An unknown concept is left verbatim rather than blanked, so a typo in a
        // template is visible instead of silently deleting part of a label.
        return resolved || whole
      }),
    [overrides],
  )

  return useMemo(() => ({ term, phrase }), [term, phrase])
}
